import type { LeadStage, SuggestionState } from "./api";

/**
 * §6.1 v2 — five stages, each defined by a fact that is true or false.
 *
 * The v1 ladder had ten, and five of them were gradations of the same two questions ("have they
 * replied", "is there a number"), so leads sat in `qualified` for weeks because nobody could tell it
 * from `negotiating`. A stage nobody can apply consistently is not a stage (D-31).
 */
export const STAGE_ORDER: readonly LeadStage[] = [
  "intake",
  "follow_up",
  "offer",
  "signed",
  "dead",
];

/** The Kanban lanes. `dead` is deliberately absent — archived leads live at /archived (D-32). */
export const BOARD_STAGES: readonly LeadStage[] = ["intake", "follow_up", "offer", "signed"];

export const STAGE_LABEL: Record<LeadStage, string> = {
  intake: "Intake",
  follow_up: "Follow Up",
  offer: "Offer",
  signed: "Signed",
  dead: "Dead",
};

/**
 * Shown beside every lane header and every stage option, so the definition travels with the control
 * instead of living in a document nobody opens while deciding.
 */
export const STAGE_HELP: Record<LeadStage, string> = {
  intake: "Replied, not yet qualified.",
  follow_up: "Qualified. No number on the table from either side.",
  offer: "A number exists — ours or theirs. Set an amount and the lead lands here.",
  signed: "A PSA exists. Set by promoting to a deal.",
  dead: "Out, with a reason.",
};

/** Terminal stages need no next action (§6.1). */
export const NO_NEXT_ACTION: readonly LeadStage[] = ["signed", "dead"];

export const DEAD_REASONS = [
  "priced_out",
  "not_interested",
  "wrong_number",
  "unsubscribed",
  "no_response",
] as const;

export type DeadReason = (typeof DEAD_REASONS)[number];

export const DEAD_REASON_LABEL: Record<DeadReason, string> = {
  priced_out: "Priced out",
  not_interested: "Not interested",
  wrong_number: "Wrong number",
  unsubscribed: "Unsubscribed",
  no_response: "No response",
};

export const DEAD_REASON_HELP: Record<DeadReason, string> = {
  priced_out: "Their number and ours will not meet.",
  not_interested: "They said no.",
  wrong_number: "Not the owner, or not this property.",
  unsubscribed: "STOP / remove me. Feeds the suppression export.",
  no_response: "Never replied, and the campaign is finished.",
};

export function stageTone(stage: LeadStage): "ok" | "info" | "warn" | "danger" | "muted" {
  switch (stage) {
    case "signed":
      return "ok";
    case "offer":
      return "info";
    case "follow_up":
      return "warn";
    case "dead":
      return "danger";
    default:
      return "muted";
  }
}

export const REPLY_CLASSES = [
  "wrong_number",
  "not_interested",
  "already_listed",
  "who_is_this",
  "neutral",
  "interested",
  "price_question",
  "hostile",
  "legal_signal",
] as const;

/**
 * §6.1 transition table, mirrored client-side so the Kanban can disable illegal drop lanes (§7.3).
 *
 * Forward-only, and every stage can die. The server is authoritative and answers 422 — this exists only
 * so the UI does not offer a move it knows will be refused. `api/services/lead_transitions.py` is the
 * original; `stages.test.ts` asserts the two agree on every pair.
 *
 * `dead → follow_up` is the revive, and the only way back (D-32).
 */
export const ALLOWED_TRANSITIONS: Record<LeadStage, readonly LeadStage[]> = {
  intake: ["follow_up", "offer", "dead"],
  follow_up: ["offer", "dead"],
  offer: ["signed", "dead"],
  signed: [],
  dead: ["follow_up"],
};

export function canMove(from: LeadStage, to: LeadStage): boolean {
  if (from === to) return true;
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export function needsNextAction(stage: LeadStage): boolean {
  return !NO_NEXT_ACTION.includes(stage);
}

/** Moving to `dead` needs a reason chip; the API refuses without one. */
export function needsDeadReason(to: LeadStage): boolean {
  return to === "dead";
}


/**
 * §7.3's Suggestion column and its filter (D-35).
 *
 * `auto_applied` is deliberately the loudest of the five, because it is the only one with an action
 * attached: the operator can still undo it. The other four are states, not invitations.
 */
export const SUGGESTION_STATES: readonly SuggestionState[] = [
  "queued",
  "auto_applied",
  "accepted",
  "rejected",
  "none",
];

export const SUGGESTION_LABEL: Record<SuggestionState, string> = {
  none: "None",
  queued: "Queued",
  auto_applied: "Auto-applied",
  accepted: "Accepted",
  rejected: "Rejected",
};

export function suggestionTone(
  state: SuggestionState,
): "ok" | "info" | "warn" | "danger" | "muted" {
  switch (state) {
    case "auto_applied":
      return "info";
    case "queued":
      return "warn";
    case "accepted":
      return "ok";
    case "rejected":
      return "muted";
    default:
      return "muted";
  }
}
